import React from "react";
import { ArrowLeft, Send, WifiOff } from "lucide-react";
import { AppButton, Callout } from "../ui";
import ExamDialog from "./ExamDialog";
import QuestionPalette, { PaletteLegend, paletteCounts } from "./QuestionPalette";

/**
 * SubmitReview — what a candidate sees before committing, and while an
 * automatic submit runs.
 *
 * Nobody submits a hiring exam from a one-line "Are you sure?": they want to see
 * what they are leaving unanswered and to be able to jump back to it. Every
 * palette cell here is a live shortcut.
 *
 * It is also the last honest moment about saving. If the autosave queue could
 * not drain, this names the answers at risk rather than letting the candidate
 * submit over the top of them; and if the submit itself cannot reach the server,
 * it says so and keeps the button, instead of leaving them on a dead screen.
 */

const AUTO_TITLE = {
  time: "Time is up",
  default: "Submitting your exam",
};

function Stat({ value, label }) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-0.5 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface tw-px-4 tw-py-3">
      <span className="tw-text-2xl tw-font-semibold tw-tabular-nums tw-leading-tight tw-text-content">{value}</span>
      <span className="tw-text-xs tw-text-content-muted">{label}</span>
    </div>
  );
}

export default function SubmitReview({
  answers,
  visited,
  currentIndex,
  sections,
  onJump,
  onBack,
  onConfirm,
  submitting,
  submitNote,
  submitError,
  negativeMarking,
  negativeMarkValue,
  unsaved,
  online,
  autoReason,
}) {
  const counts = paletteCounts(answers, currentIndex, visited);
  const unanswered = counts.total - counts.totalAnswered;
  const marked = counts.marked + counts.answeredMarked;
  const auto = !!autoReason;

  const title = auto ? (AUTO_TITLE[autoReason] || AUTO_TITLE.default) : "Review and submit";
  const description = auto
    ? "Your answers are being submitted. Please keep this page open."
    : "You can still go back and change any answer.";

  return (
    <ExamDialog
      open
      size="lg"
      title={title}
      description={description}
      dismissible={!auto && !submitting}
      onClose={onBack}
      footer={
        <>
          {!auto && (
            <AppButton type="button" variant="outline" onClick={onBack} disabled={submitting}>
              <ArrowLeft size={15} aria-hidden="true" /> Back to exam
            </AppButton>
          )}
          {(!auto || submitError) && (
            <AppButton type="button" variant="default" onClick={onConfirm} disabled={submitting}>
              <Send size={15} aria-hidden="true" />
              {submitting ? "Submitting…" : submitError ? "Try again" : "Submit exam"}
            </AppButton>
          )}
          {auto && !submitError && (
            <span role="status" className="tw-text-sm tw-text-content-muted">
              {submitNote || "Submitting…"}
            </span>
          )}
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-5">
        <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-4">
          <Stat value={counts.totalAnswered} label="Answered" />
          <Stat value={unanswered} label="Not answered" />
          <Stat value={marked} label="Marked for review" />
          <Stat value={counts.total} label="Total questions" />
        </div>

        {submitError && (
          <Callout tone="danger" role="alert">{submitError}</Callout>
        )}

        {/* The scoring rule in words. "Negative marking is on" means nothing to
            a candidate under time pressure; the arithmetic does. */}
        {!auto && unanswered > 0 && (
          <Callout tone="warning">
            {unanswered} question{unanswered === 1 ? " is" : "s are"} unanswered.
            Unanswered questions score 0
            {negativeMarking && negativeMarkValue > 0
              ? `, but a wrong answer loses ${negativeMarkValue} mark${negativeMarkValue === 1 ? "" : "s"}.`
              : "."}
          </Callout>
        )}

        {unsaved?.length > 0 && (
          <Callout tone="danger" role="alert" icon={online ? undefined : <WifiOff size={15} />}>
            {unsaved.length} answer{unsaved.length === 1 ? "" : "s"} could not be saved to the server
            yet{online ? "" : " because you are offline"}. Wait a moment and try again — submitting
            now may lose {unsaved.length === 1 ? "it" : "them"}.
          </Callout>
        )}

        {!auto && (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <QuestionPalette
              answers={answers}
              currentIndex={currentIndex}
              visited={visited}
              sections={sections}
              onNavigate={onJump}
            />
            <PaletteLegend counts={counts} inline className="tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pt-4" />
          </div>
        )}
      </div>
    </ExamDialog>
  );
}
